<script lang="ts" setup>
import type { MerchantShopRenewInfoVO } from '@/api/business/types/table'
import { useVModel } from '@vueuse/core'
import dayjs from 'dayjs'
import { computed, ref, toRef } from 'vue'
import BuyQrCodeDialog from '@/components/dialog/BuyQrCodeDialog.vue'
import { useUserStore } from '@/store/modules/user'
import { getEnv } from '@/utils'
import { getShortDate } from '@/utils/datetime'

const props = defineProps<{
  show: boolean
  expiringItems: MerchantShopRenewInfoVO[]
}>()

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'renew', item: MerchantShopRenewInfoVO): void
}>()

const showBuyDialog = ref(false)
const qrCodeUrl = ref('')
const searchKeyword = ref('')
const userStore = useUserStore()
const dialogVisible = useVModel(props, 'show', emit)
const shop = computed(() => {
  return userStore.getShopInfo()
})

const expiringItems = toRef(props, 'expiringItems')
const handleClose = () => {
  dialogVisible.value = false
}

const handleRenew = (item: MerchantShopRenewInfoVO) => {
  qrCodeUrl.value = `https://api-dev.teahouses.cn/teashop_merchant/to_spu/${getEnv()}?spuId=${item.productId}`
  searchKeyword.value = item.productName
  showBuyDialog.value = true
}

function getRemainingText(item: MerchantShopRenewInfoVO) {
  switch (item.productType) {
    case 1:
    case 2: {
      const diffDays = dayjs(item.expirationDate).diff(dayjs(), 'days')
      return diffDays >= 0 ? `剩余${diffDays}天` : `已过期${Math.abs(diffDays)}天`
    }
    case 4:
      return item.messageNumber + '条'
    default:
      return '不足'
  }
}
</script>

<template>
  <div>
    <el-dialog
      v-model="dialogVisible"
      :append-to-body="true"
      width="560px"
      @close="handleClose"
    >
      <template #header>
        <div class="text-size-20px">
          商品即将到期通知
        </div>
      </template>
      <div class="notice-content">
        <div class="notice-body">
          <div>
            尊敬的客户：
          </div>
          <div>
            您的【{{ shop?.shopName }}】店铺以下商品即将到期，避免影响使用请前往采购商城-"管理系统"分类进行续费。
          </div>
        </div>
        <div class="notice-items">
          <template v-if="expiringItems.length">
            <div v-for="(item, index) in expiringItems" :key="index" class="notice-item">
              <span class="item-name">{{ item.productName }}{{ getRemainingText(item) }}</span>
              <el-link type="primary" underline @click="handleRenew(item)">前往续费</el-link>
            </div>
          </template>
        </div>
      </div>
      <template #footer>
        <div class="flex items-center justify-between">
          <div class="text-size-14px color-#909399">
            {{ getShortDate(new Date(), 'YYYY-MM-DD', 'HH:mm') }}
          </div>
          <el-button @click="handleClose">关闭</el-button>
        </div>
      </template>
    </el-dialog>
    <BuyQrCodeDialog
      v-model:show="showBuyDialog"
      :qr-code-url="qrCodeUrl"
      :search-keyword="searchKeyword"
      title="扫码续费"
    />
  </div>
</template>

<style scoped>
.notice-content {
  padding: 20px;
  padding-bottom: 0;
}

.notice-body {
  margin-bottom: 20px;
  font-size: 16px;
  line-height: 1.5;
  color: #303133;
}

.notice-items {
  margin: auto 35px;

  .notice-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 10px;
  }
}

.item-name {
  margin-right: 10px;
  font-size: 16px;
  font-weight: 600;
}
</style>
